import { useQuery, UseQueryResult, useQueryClient, UseMutationResult, useMutation } from '@tanstack/react-query';

import { StructureType, StructureTypeResponse } from '../@types';
import {
  getStructureTypesByCategoryCode,
  createStructureType,
  getStructureTypeByCode,
  updateStructureType,
  getStructureTypeByCodeForUpdate
} from '../api/structureTypes';
import { StructureTypeByCodeKey, StructureTypeKey } from '../configs/structure-type';

export const useGetStructureTypesByCategoryCode = (catagoryCode: string): UseQueryResult<StructureTypeResponse[]> => {
  return useQuery({
    queryKey: [StructureTypeKey],
    queryFn: () => getStructureTypesByCategoryCode(catagoryCode),
    staleTime: 60 * 60 * 1000
  });
};

export const useCreateStructureType = (): UseMutationResult<StructureTypeResponse, Error, Partial<StructureType> & Pick<StructureType, 'code' | 'name'> & Record<string, unknown>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<StructureType> & Pick<StructureType, 'code' | 'name'> & Record<string, unknown>) => {
      return await createStructureType(data as StructureType);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [StructureTypeKey]
      });
    }
  });
};

export const useUpdateStructureType = (): UseMutationResult<StructureTypeResponse, Error, Partial<StructureType> & Pick<StructureType, 'code' | 'name'> & Record<string, unknown>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<StructureType> & Pick<StructureType, 'code' | 'name'> & Record<string, unknown>) => {
      return await updateStructureType(data.code, data as StructureType);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [StructureTypeKey]
      });
    }
  });
};

export const useGetStructureTypesByCode = (code: string): UseQueryResult<StructureTypeResponse> => {
  return useQuery({
    queryKey: [StructureTypeKey + code],
    queryFn: () => getStructureTypeByCode(code),
    enabled: code !== '-1' && code !== '',
  });
};

export const useGetStructureTypesByCodeForUpdate = (code: string): UseQueryResult<StructureTypeResponse> => {
  return useQuery({
    queryKey: [StructureTypeKey + code],
    queryFn: () => getStructureTypeByCodeForUpdate(code),
  });
};

export const useGetStructureTypesExistanceByCode = (code: string): UseQueryResult<StructureTypeResponse> => {
  return useQuery({
    queryKey: [StructureTypeByCodeKey, code],
    queryFn: () => getStructureTypeByCodeForUpdate(code),
    retry: false,
    enabled: false
  });
};